import clsx from 'clsx'
import React, { useState, ReactNode } from 'react'
import { Card } from '@/components/Card'
import { isAfter, subMonths } from 'date-fns'

interface RiskCardProps<T> {
  title: string
  items: T[]
  separator?: boolean
  renderItem: (item: T, old: boolean) => ReactNode
  getDate: (item: T) => Date
}

export function RiskCard<T>({
  title,
  items,
  renderItem,
  getDate,
  separator
}: RiskCardProps<T>) {
  const [showOld, setShowOld] = useState(false)
  const threshold = subMonths(new Date(), 3)

  const sorted = [...items].sort(
    (a, b) => getDate(b).getTime() - getDate(a).getTime()
  )

  const recent = sorted.filter((item) => isAfter(getDate(item), threshold))
  const old = sorted.filter((item) => !isAfter(getDate(item), threshold))

  return (
    <Card
      title={
        <div className="d-flex align-items-center justify-content-between">
          <span className="me-2">{title}</span>
          <span className="badge badge-primary text-white">
            {recent.length}
            <i className="ms-1">recents</i>
          </span>
        </div>
      }
      collapsable
      id={`${title.toLowerCase().replace(/\s+/g, '-')}-card`}
      collapse
      className="mb-2"
    >
      {items.length === 0 ? (
        <>No data</>
      ) : (
        <div className={clsx({
          'd-flex flex-wrap gap-2': !separator,
          'd-block': separator
        })}>
          {recent.map((item, i) => (
            <React.Fragment key={`recent-${i}`}>
              {separator && i > 0 && (
                <div className="separator separator-dashed my-5"></div>
              )}
              {renderItem(item, false)}
            </React.Fragment>
          ))}

          {old.length > 0 && (
            <>
              <span
                onClick={() => setShowOld((prev) => !prev)}
                role="button"
                className="badge badge-info"
              >
                {showOld ? 'Hide older' : `Older (${old.length})`}
              </span>

              {showOld && old.map((item, i) => (
                <React.Fragment key={`recent-${i}`}>
                  {separator && i > 0 && (
                    <div className="separator separator-dashed my-5"></div>
                  )}
                  {renderItem(item, true)}
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      )}
    </Card>
  )
}
